
import _toArray from 'lodash/toArray';
import _map from 'lodash/map';
import _flatten from 'lodash/flatten';
import _union from 'lodash/union';
import _forEach from 'lodash/forEach';

import urlTool from '@/tools/url';
import promiseJQueryAdapter from '@/tools/promiseJQueryAdapter';
import Shop from '@/shop';
import createStorage from '@/storage';
import getTasks from '@/ajax/product/helpers/getTasks';

const currency = Shop.config.config.currency_code;
const maxOrderLinesCount = Shop.config.config.max_order_lines_count;
const Store = createStorage({ name: 'Products-' + currency });

/**
 * @memberof module:ajaxAPI/product
 * @alias getList
 *
 * @description
 * Получение информации о списке товаров.
 *
 * Важно! Отсутствует поле "подробное описание".
 *
 * @param {Array} idsArray - список id товаров, за раз моно получить информацию не более чем о 100 товарах
 *
 * @param {Object} options - опции:
 * noCache - true/false - указываем если не нужно сохранять товары в storage (для клиентов)
 * withoutManyVariants - true/false - указываем если нужно получить только первый вариант для каждого товара
 * isCommonRequest - true/false - указываем если запрос идёт из коммона, чтобы мы не пытались сохранить товары в storage отсюда
 *
 * @return {Promise}

 * @example
 * {@lang javascript}
 * ajaxAPI.product.getList([123456,123457,123458,123459])
 *  .done(function (onDone) {console.log('onDone: ', onDone) })
 *  .fail(function (onFail) {console.log('onFail: ', onFail) });
 */

// Устаналиваем лимит товаров в одном запросе + 200
const limitProducts = maxOrderLinesCount + 200;

// Объект для всех ID товаров
let idsArrayObject = {};

export default (idsArray, options = {}) => {
  const lang = urlTool.getKeyValue('lang') || '';

  // Приводим к типу массив (мало ли что там прилетело)
  let processedIdsArray = _toArray(idsArray);

  // Если лимит товаров в одном запросе превышен, то обрезаем массив, и сообщаем об этом в консоли
  if (processedIdsArray.length > limitProducts) {
    console.warn(`Превышен лимит на ${limitProducts} товаров в одном запросе. Возвращаем первые ${limitProducts} товаров.`);
    processedIdsArray = processedIdsArray.slice(0, limitProducts);
  }

  // Заводим массивы для разбивки товаров
  let idsArrayFiltered = [];
  let cachedIds = [];

  // Разбиваем товары на две пачки: те которые приходили в предыдущих запросах будем доставать из storage, а те которых ещё не было будем запрашивать с бэка
  processedIdsArray.forEach(id => {
    if (!idsArrayObject[id]) {
      idsArrayObject[id] = true;
      idsArrayFiltered.push(id);
    } else {
      cachedIds.push(id.toString());
    }
  });

  // Достаём товары из Storage. Те товары которых в нём не оказалось запрашиваем, далее склеиваем и отдаём полученные массивы. Если в кэше искать нечего, то запрашиваем товары и отдаём их
  if (cachedIds.length && !options.noCache) {
    return promiseJQueryAdapter(resolve => {
      Store.getItems(cachedIds)
        .then(storageProducts => {
          // Те товары которые не нашли в кэше пушим в массив для отправки запроса
          cachedIds.forEach(id => {
            if (!storageProducts[id]) {
              idsArrayFiltered.push(parseInt(id));
            }
          });

          /*
           * Склеиваем ответы полученные из getTasks.
           *
           * Проходимся по всем получившимся промисам, дергаем их
           */

          Promise.all(getTasks(idsArrayFiltered, options))
            .then(values => {
              let cached = _union(_flatten(_toArray(storageProducts)));

              let haventCached = _flatten(_union(_map(values, 'products')));

              _forEach(haventCached, product => {
                if (product && lang) {
                  product.url += '?lang=' + lang;
                }
              });

              if (!options.isCommonRequest && !options.noCache) {
                window.Products._storage.saveProducts(haventCached);
              }

              return [ ...cached, ...haventCached ];
              /*
               * Получаем ответы ото ВСЕХ промисов.
               * Приводим к типу массив, на случай если ответы все кривые и там - пустота
               * Вытаскиваем массив с продуктами, склеиваем все массивы + товары из Storage и выдаем наружу
               */
            })
            .then(products => {
              resolve(products);
            })
            .catch(() => {
              resolve([]);
            });
        });
    });
  }

  /*
  * Склеиваем ответы полученные из getTasks.
  *
  * Проходимся по всем получившимся промисам, дергаем их
  */
  return promiseJQueryAdapter((resolve) => {
    Promise.all(getTasks(processedIdsArray, options))
      .then(values => {
        let valuesObject = _union(_flatten(_map(_toArray(values), response => response.products)));

        _forEach(valuesObject, product => {
          if (product && lang) {
            product.url += '?lang=' + lang;
          }
        });

        if (!options.isCommonRequest && !options.noCache) {
          window.Products._storage.saveProducts(valuesObject);
        }

        return valuesObject;
        /*
         * Получаем ответы ото ВСЕХ промисов.
         * Приводим к типу массив, на случай если ответы все кривые и там - пустота
         * Вытаскиваем массив с продуктами, склеиваем все массивы и выдаем наружу
         */
      })
      .then(products => {
        resolve(products);
      })
      .catch(() => {
        resolve([]);
      });
  });
};
